/**
 * Video Editor — Storyboard (the old Pick + Refine surfaces, merged).
 *
 * One screen that shows what the rendered video WILL be, before rendering: a
 * shot-by-shot storyboard with a thumbnail + co-pilot description per shot,
 * the cut's metadata, inline reorder/drop, post-render per-shot re-render, and
 * the render button. This is where the operator makes decisions — the old
 * Pick/Refine steps showed almost nothing, so they were merged into this.
 */
import React, { useState, useEffect, useCallback } from 'react';
import type { VideoProject, EditPlan, PlanSegment } from '../types';
import type { VideoEditorApi } from '../videoEditorApi';
import { SurfaceHeader, formatDuration } from './parts';
import MusicPicker from './MusicPicker';

interface Props {
  project: VideoProject;
  api: VideoEditorApi;
  onRefresh: () => Promise<any>;
  onRunPlan: () => void;
  onRender: () => void;
  onRenderSegment: (index: number) => void;
  activeJob: any;
}

// A working segment carries its saved-plan index, so the thumbnail and
// description (fetched against the saved plan) follow it through local
// reorders before a save.
type WorkSeg = PlanSegment & { _key: string; _origIndex: number };

// Pull a {segments,...} plan out of a variant object of uncertain shape.
function variantPlan(v: any): EditPlan | null {
  if (!v) return null;
  if (v.editPlan && Array.isArray(v.editPlan.segments)) return v.editPlan;
  if (v.edit_plan && Array.isArray(v.edit_plan.segments)) return v.edit_plan;
  if (Array.isArray(v.segments)) return { ...v, segments: v.segments };
  return null;
}

function pointSeconds(numMs?: number, val?: number | string): number {
  if (typeof numMs === 'number') return numMs / 1000;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const m = val.match(/([\d.]+)/);
    if (m) return parseFloat(m[1]) || 0;
  }
  return 0;
}
const inSeconds = (s: PlanSegment) => pointSeconds(s.in_point_ms, s.in_point);
const outSeconds = (s: PlanSegment) => pointSeconds(s.out_point_ms, s.out_point);
function segDuration(s: PlanSegment): number {
  const d = outSeconds(s) - inSeconds(s);
  return d > 0 ? d : 0;
}
function clipName(s: PlanSegment, i: number): string {
  return s.clip_file || s.clip_id || `clip ${i + 1}`;
}

// A signature of the SAVED plan — drives thumbnail/description refetch. Local
// unsaved reorders do not change it, so they do not trigger a slow re-fetch.
function planSignature(plan?: EditPlan | null): string {
  const segs = (plan && Array.isArray(plan.segments)) ? plan.segments : [];
  return segs.map((s, i) => `${clipName(s, i)}@${inSeconds(s)}-${outSeconds(s)}`).join('|');
}

// The soundtrack label for the cut-metadata stat. The operator's library
// selection (project.music) is what the next render will apply, so it wins
// over whatever the planner last wrote into editPlan.music.
function musicLabel(project: VideoProject): string {
  const sel: any = project.music;
  if (sel && sel.title) {
    return sel.audioMode === 'only' ? `${sel.title} (music only)` : sel.title;
  }
  const m: any = project.editPlan && project.editPlan.music;
  if (!m) return 'No music';
  if (typeof m === 'string') return m;
  if (typeof m === 'object') return m.name || m.track || m.title || 'Music';
  return 'Music';
}

const StoryboardSurface: React.FC<Props> = ({
  project, api, onRefresh, onRunPlan, onRender, onRenderSegment, activeJob,
}) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const savedPlan = (project.editPlan && Array.isArray(project.editPlan.segments))
    ? project.editPlan : null;
  const variants = project.variants || [];
  const hasPlan = !!savedPlan;
  const hasRender = !!(project.renderOutput && project.renderOutput.vmPath);
  const sig = planSignature(savedPlan);

  // Working copy of the segments — reorder/drop before save.
  const [segments, setSegments] = useState<WorkSeg[]>([]);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    const segs = savedPlan ? savedPlan.segments : [];
    setSegments(segs.map((s, i) => ({ ...s, _key: s.id || `s${i}`, _origIndex: i })));
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  // Thumbnails + co-pilot descriptions for the saved plan, keyed by saved index.
  const [thumbs, setThumbs] = useState<Record<number, string | null>>({});
  const [descs, setDescs] = useState<Record<number, { title?: string; description?: string }>>({});
  const [thumbsLoading, setThumbsLoading] = useState(false);
  const [descState, setDescState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');

  const loadThumbs = useCallback(async () => {
    setThumbsLoading(true);
    try {
      const list = await api.loadStoryboardThumbnails(project._id);
      const map: Record<number, string | null> = {};
      list.forEach(t => { map[t.index] = t.dataUrl; });
      setThumbs(map);
    } catch { /* placeholders remain */ }
    finally { setThumbsLoading(false); }
  }, [api, project._id]);

  const loadDescs = useCallback(async () => {
    setDescState('loading');
    try {
      const shots = await api.describeStoryboard(project._id);
      const map: Record<number, { title?: string; description?: string }> = {};
      shots.forEach(s => {
        if (typeof s.index === 'number') map[s.index] = { title: s.title, description: s.description };
      });
      setDescs(map);
      setDescState('done');
    } catch {
      setDescState('error');
    }
  }, [api, project._id]);

  useEffect(() => {
    if (!sig) return;
    loadThumbs();
    loadDescs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  async function choose(plan: EditPlan) {
    try {
      setBusy(true); setErr(null);
      await api.patchEditPlan(project._id, plan);
      await onRefresh();
    } catch (e: any) {
      setErr(e.response?.data?.error || e.message || 'Could not select that cut');
    } finally { setBusy(false); }
  }

  async function saveSegments() {
    try {
      setBusy(true); setErr(null);
      const clean = segments.map(s => {
        const c: any = { ...s };
        delete c._key; delete c._origIndex;
        return c as PlanSegment;
      });
      const plan: EditPlan = { ...(project.editPlan || {}), segments: clean };
      await api.patchEditPlan(project._id, plan);
      await onRefresh();
    } catch (e: any) {
      setErr(e.response?.data?.error || e.message || 'Could not save the cut');
    } finally { setBusy(false); }
  }

  function mutate(next: WorkSeg[]) { setSegments(next); setDirty(true); }
  const removeSeg = (i: number) => mutate(segments.filter((_, idx) => idx !== i));
  const moveSeg = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= segments.length) return;
    const next = segments.slice();
    [next[i], next[j]] = [next[j], next[i]];
    mutate(next);
  };

  const jobRunning = !!activeJob && (activeJob.status === 'queued' || activeJob.status === 'running');
  const totalSec = segments.reduce((a, s) => a + segDuration(s), 0)
    || (project.editPlan && project.editPlan.target_duration_seconds) || 0;

  // No plan yet (defensive — the flow normally lands here with a plan).
  if (!hasPlan && variants.length === 0) {
    return (
      <div>
        <SurfaceHeader
          eyebrow="Step 2 of 3"
          title="Storyboard"
          subtitle="No cut yet — generate one from the brief and the analysed footage."
        />
        {err && <div className="ve-banner ve-banner--error">{err}</div>}
        <div className="ve-panel ve-job">
          {jobRunning
            ? <><div className="ve-job__bar"><span /></div>
                <div className="ve-job__meta"><strong>Generating the cut…</strong></div></>
            : <button className="ve-btn ve-btn--primary" disabled={busy} onClick={onRunPlan}>
                Generate a cut →
              </button>}
        </div>
      </div>
    );
  }

  return (
    <div>
      <SurfaceHeader
        eyebrow="Step 2 of 3"
        title={hasRender ? 'Storyboard — refine & re-render' : 'Your storyboard'}
        subtitle={hasRender
          ? 'Tweak the cut and re-render, or re-render a single shot to iterate fast.'
          : 'This is what the AI will render. Reorder or drop shots, or ask the co-pilot to change it — then render.'}
      />
      {err && <div className="ve-banner ve-banner--error">{err}</div>}

      {/* Multi-variant picking — shows once the pipeline emits >1 cut. */}
      {variants.length > 1 && (
        <div className="ve-variants">
          {variants.map((v: any, i: number) => {
            const plan = variantPlan(v);
            return (
              <div className={`ve-variant${i === 0 ? ' ve-variant--recommended' : ''}`} key={v.id || i}>
                {i === 0 && <span className="ve-variant__tag">AI's pick</span>}
                <h3>{v.label || `Cut ${i + 1}`}</h3>
                <p className="ve-muted">{v.vibe_text || ''}</p>
                <button className="ve-btn ve-btn--primary" disabled={busy || !plan}
                  onClick={() => plan && choose(plan)}>Use this cut</button>
              </div>
            );
          })}
        </div>
      )}

      {/* Cut metadata — the shape of the video at a glance. */}
      <div className="ve-panel">
        <div className="ve-statgrid">
          <div className="ve-stat">
            <div className="ve-stat__v">{formatDuration(totalSec) || '—'}</div>
            <div className="ve-stat__l">Runtime</div>
          </div>
          <div className="ve-stat">
            <div className="ve-stat__v">{segments.length}</div>
            <div className="ve-stat__l">Shots</div>
          </div>
          <div className="ve-stat">
            <div className="ve-stat__v">{musicLabel(project)}</div>
            <div className="ve-stat__l">Music</div>
          </div>
          <div className="ve-stat">
            <div className="ve-stat__v">{project.editPlan?.subtitles ? 'On' : 'Off'}</div>
            <div className="ve-stat__l">Captions</div>
          </div>
          <div className="ve-stat">
            <div className="ve-stat__v">{project.format?.aspect || project.editPlan?.target_format || '—'}</div>
            <div className="ve-stat__l">Aspect</div>
          </div>
        </div>
      </div>

      {/* Shot-by-shot storyboard. */}
      <div className="ve-panel">
        <h3>
          Shots <span className="ve-muted">({segments.length})</span>
          {descState === 'loading' && <span className="ve-muted"> · the co-pilot is describing the shots…</span>}
          {descState === 'error' && (
            <span className="ve-muted"> · descriptions unavailable
              {' '}<button className="ve-link" onClick={loadDescs}>retry</button></span>
          )}
          {descState === 'done' && (
            <button className="ve-link" style={{ marginLeft: 8 }} onClick={loadDescs}>re-describe</button>
          )}
        </h3>
        {segments.length === 0 && <p className="ve-muted">This cut has no shots.</p>}
        {segments.map((seg, i) => {
          const thumb = thumbs[seg._origIndex];
          const desc = descs[seg._origIndex];
          return (
            <div className="ve-shot" key={seg._key}>
              <span className="ve-shot__idx">{i + 1}</span>
              {thumb
                ? <img className="ve-shot__thumb" src={thumb} alt={`Shot ${i + 1}`} />
                : <span className="ve-shot__thumb ve-shot__thumb--empty">
                    {thumbsLoading ? '…' : '🎞'}
                  </span>}
              <div className="ve-shot__body">
                <div className="ve-shot__title">{desc?.title || clipName(seg, i)}</div>
                {desc?.description
                  ? <p className="ve-shot__desc">{desc.description}</p>
                  : descState === 'loading'
                    ? <p className="ve-shot__desc ve-muted">Describing this shot…</p>
                    : null}
                <div className="ve-shot__meta">
                  {clipName(seg, i)}
                  {segDuration(seg) > 0 ? ` · ${segDuration(seg).toFixed(1)}s` : ''}
                </div>
              </div>
              <span className="ve-shot__actions">
                {hasRender && (
                  <button className="ve-iconbtn"
                    title={dirty ? 'Save your edits first' : 'Re-render just this shot'}
                    disabled={busy || dirty} onClick={() => onRenderSegment(i)}>↻</button>
                )}
                <button className="ve-iconbtn" disabled={i === 0} onClick={() => moveSeg(i, -1)}>↑</button>
                <button className="ve-iconbtn" disabled={i === segments.length - 1}
                  onClick={() => moveSeg(i, 1)}>↓</button>
                <button className="ve-iconbtn ve-iconbtn--danger" onClick={() => removeSeg(i)}>✕</button>
              </span>
            </div>
          );
        })}
        {dirty && (
          <div className="ve-actions" style={{ marginTop: 12 }}>
            <button className="ve-btn ve-btn--primary" disabled={busy} onClick={saveSegments}>
              {busy ? 'Saving…' : 'Save changes'}
            </button>
            <span className="ve-muted">Unsaved edits — save before rendering</span>
          </div>
        )}
      </div>

      {/* Background music — picked from the CC0 library, applied next render. */}
      <MusicPicker project={project} api={api} onRefresh={onRefresh} />

      <div className="ve-startbar">
        <span className="ve-muted">
          Want a different take?{' '}
          <button className="ve-link" onClick={onRunPlan}>Re-generate the cut</button>.
        </span>
        <button
          className="ve-btn ve-btn--primary"
          disabled={busy || dirty || segments.length === 0}
          onClick={onRender}
          title={dirty ? 'Save your changes first' : ''}
        >{hasRender ? 'Re-render this cut →' : 'Render this cut →'}</button>
      </div>
    </div>
  );
};

export default StoryboardSurface;
